import type {
	CustomFrequencyState,
	FrequencyPreset,
} from "../lib/track/serializeRRule";
import type { OnboardingAnswers } from "../types/onboarding";

export interface ValidationError {
	field: string;
	message: string;
}

export interface ValidationResult {
	isValid: boolean;
	errors: ValidationError[];
}

/**
 * Validates the personal info form fields (without name and location - those are in setup-profile)
 */
export function validatePersonalInfoForm(
	answers: OnboardingAnswers,
): ValidationResult {
	const errors: ValidationError[] = [];

	// Check required fields
	if (!answers.dayOfBirth || !answers.monthOfBirth || !answers.yearOfBirth) {
		errors.push({ field: "dateOfBirth", message: "Date of birth is required" });
	}

	if (!answers.languagePreference || answers.languagePreference.trim() === "") {
		errors.push({
			field: "languagePreference",
			message: "Language preference is required",
		});
	}

	return {
		isValid: errors.length === 0,
		errors,
	};
}

/**
 * Validates the setup profile form fields (name required, location optional)
 */
export function validateSetupProfileForm(
	answers: OnboardingAnswers,
): ValidationResult {
	const errors: ValidationError[] = [];

	// Check required fields - only name is required, location is optional
	if (!answers.firstName || answers.firstName.trim() === "") {
		errors.push({ field: "firstName", message: "Name is required" });
	}

	// Location is optional, no validation needed

	return {
		isValid: errors.length === 0,
		errors,
	};
}

/**
 * Checks if setup profile form has all required fields filled (name only, location is optional)
 */
export function hasSetupProfileCompleted(answers: OnboardingAnswers): boolean {
	return !!(answers.firstName && answers.firstName.trim() !== "");
}

/**
 * Validates the setup profile details form fields (year and gender both required)
 */
export function validateSetupProfileDetailsForm(
	answers: OnboardingAnswers,
): ValidationResult {
	const errors: ValidationError[] = [];

	// Check year of birth - should be 4 digits
	if (!answers.yearOfBirth || answers.yearOfBirth === "") {
		errors.push({ field: "yearOfBirth", message: "Year of birth is required" });
	} else if (
		typeof answers.yearOfBirth === "string" &&
		answers.yearOfBirth.length !== 4
	) {
		errors.push({
			field: "yearOfBirth",
			message: "Year must be 4 digits",
		});
	}

	// Check gender
	if (!answers.gender || answers.gender === "") {
		errors.push({ field: "gender", message: "Gender is required" });
	}

	return {
		isValid: errors.length === 0,
		errors,
	};
}

/**
 * Checks if setup profile details form has all required fields filled
 */
export function hasSetupProfileDetailsCompleted(
	answers: OnboardingAnswers,
): boolean {
	return !!(
		answers.yearOfBirth &&
		typeof answers.yearOfBirth === "string" &&
		answers.yearOfBirth.length === 4 &&
		answers.gender &&
		answers.gender !== ""
	);
}

/**
 * Validates a profile selection
 */
export function validateProfileSelection(value: string): ValidationResult {
	const errors: ValidationError[] = [];

	if (!value || value.trim() === "") {
		errors.push({
			field: "userType",
			message: "Profile selection is required",
		});
	}

	return {
		isValid: errors.length === 0,
		errors,
	};
}

/**
 * Checks if personal info form has all required fields filled (without name and location)
 */
export function hasPersonalInfoCompleted(answers: OnboardingAnswers): boolean {
	return !!(
		answers.dayOfBirth &&
		answers.monthOfBirth &&
		answers.yearOfBirth &&
		answers.languagePreference &&
		answers.languagePreference.trim() !== ""
	);
}

/**
 * Gets validation error for a specific field
 */
export function getFieldError(
	errors: ValidationError[],
	fieldName: string,
): string | undefined {
	return errors.find((error) => error.field === fieldName)?.message;
}

// ─────────────────────────────────────────────────────────────────────
// Phase 37 — Add Event form validators (ADD-07, D-07, D-08).
//
// Per-form `validateXForm(state)` returns ValidationResult — each error.message
// is an i18n key (e.g. `event.add.errors.required`) that the form layer
// translates via `t(error.message)`. era-api zod schemas are the source-of-truth
// boundary; FE validators duplicate the required-fields rules only (defence-in-depth).
// ─────────────────────────────────────────────────────────────────────

export interface AppointmentTestFormState {
	formType: "appointment" | "test";
	subtype: string | null;
	date: Date | null;
	time: string | null; // "HH:mm"
	location: string;
	contactId: string | null; // UUID from ContactPicker
	notes: string;
}

export interface MedicationFormState {
	medicationName: string;
	subtype: string | null;
	date: Date | null;
	time: string | null;
	dosage: string;
	frequencyPreset: FrequencyPreset;
	customFrequency?: CustomFrequencyState;
	contactId: string | null; // OPTIONAL: prescribed-by
	notes: string;
}

export interface CycleFormState {
	subtype: string | null;
	date: Date | null;
	notes: string;
}

export interface AdminFormState {
	subtype: string | null;
	date: Date | null;
	time: string | null;
	contactId: string | null;
	notes: string;
}

const REQUIRED_KEY = "event.add.errors.required";

export function validateAppointmentTestForm(
	state: AppointmentTestFormState,
	options: { contactRequired?: boolean } = {},
): ValidationResult {
	// D2C users may not have a doctor/contact, so the field is optional for them
	// (gate passed in by the caller via useUserType). Defaults to required.
	const { contactRequired = true } = options;
	const errors: ValidationError[] = [];
	if (!state.subtype) errors.push({ field: "subtype", message: REQUIRED_KEY });
	if (!state.date) errors.push({ field: "date", message: REQUIRED_KEY });
	if (!state.time) errors.push({ field: "time", message: REQUIRED_KEY });
	if (!state.location || state.location.trim() === "") {
		errors.push({ field: "location", message: REQUIRED_KEY });
	}
	if (contactRequired && !state.contactId)
		errors.push({ field: "contactId", message: REQUIRED_KEY });
	return { isValid: errors.length === 0, errors };
}

export function validateMedicationForm(
	state: MedicationFormState,
): ValidationResult {
	const errors: ValidationError[] = [];
	if (!state.medicationName || state.medicationName.trim() === "") {
		errors.push({ field: "medicationName", message: REQUIRED_KEY });
	}
	// subtype is auto-derived from the combined "Medication Name + Type" dropdown
	// (see MedicationForm) so no separate validation gate.
	if (!state.date) errors.push({ field: "date", message: REQUIRED_KEY });
	if (!state.time) errors.push({ field: "time", message: REQUIRED_KEY });
	if (state.frequencyPreset === "custom") {
		const c = state.customFrequency;
		if (!c) {
			errors.push({ field: "customFrequency", message: REQUIRED_KEY });
		} else {
			if (c.weekdays.length === 0) {
				errors.push({
					field: "customFrequency.weekdays",
					message: REQUIRED_KEY,
				});
			}
			const hasUntil = c.endMode === "until" && !!c.until;
			const hasCount =
				c.endMode === "count" && typeof c.count === "number" && c.count >= 1;
			if (!hasUntil && !hasCount) {
				errors.push({ field: "customFrequency.end", message: REQUIRED_KEY });
			}
		}
	}
	// contactId is OPTIONAL on medication form (prescribed-by) per UI-SPEC + RESEARCH §Validation Rule Table
	return { isValid: errors.length === 0, errors };
}

export function validateCycleForm(state: CycleFormState): ValidationResult {
	const errors: ValidationError[] = [];
	if (!state.subtype) errors.push({ field: "subtype", message: REQUIRED_KEY });
	if (!state.date) errors.push({ field: "date", message: REQUIRED_KEY });
	return { isValid: errors.length === 0, errors };
}

export function validateAdminForm(state: AdminFormState): ValidationResult {
	const errors: ValidationError[] = [];
	if (!state.subtype) errors.push({ field: "subtype", message: REQUIRED_KEY });
	if (!state.date) errors.push({ field: "date", message: REQUIRED_KEY });
	if (!state.time) errors.push({ field: "time", message: REQUIRED_KEY });
	if (!state.contactId)
		errors.push({ field: "contactId", message: REQUIRED_KEY });
	return { isValid: errors.length === 0, errors };
}
